/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { signupCustomer } from 'school-space-client/api';
import { Button, Spacer } from 'school-space-client/components';
import GooglePlacesAutocomplete from 'dashboard/components/GooglePlacesAutocomplete';

const phoneRegExp = /^\s*((?:[+](?:\s?\d)(?:[-\s]?\d)|0)?(?:\s?\d)(?:[-\s]?\d){9}|[(](?:\s?\d)(?:[-\s]?\d)+\s*[)](?:[-\s]?\d)+)\s*$/;

const Page1Schema = Yup.object().shape({
    firstName: Yup.string()
        .min(2, 'Too Short!')
        .max(50, 'Too Long!')
        .required('Required'),
    lastName: Yup.string()
        .min(2, 'Too Short!')
        .max(50, 'Too Long!')
        .required('Required'),
    phone: Yup.string()
        .matches(phoneRegExp, 'Phone number is not valid')
        .required('Required'),
    email: Yup.string()
        .email('Invalid email')
        .required('Required'),
    organisation: Yup.string(),
    addressLine1: Yup.string()
        .min(2, 'Too Short!')
        .required('Required'),
    addressLine2: Yup.string(),
    townCity: Yup.string(),
    postCode: Yup.string()
        .min(2, 'Too Short!')
        .required('Required'),
    country: Yup.string()
        .min(2, 'Too Short!')
        .required('Required')
});

const Page2Schema = Yup.object().shape({
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Required')
});

const css = {
    container: {
        maxWidth: '600px',
        margin: '0 auto',
        padding: '40px 20px'
    },
    heading: {
        fontSize: '28px',
        fontWeight: 'bold',
        marginBottom: '10px'
    },
    subheading: {
        fontSize: '16px',
        color: '#666',
        marginBottom: '30px'
    },
    formGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        marginBottom: '5px',
        fontWeight: '500'
    },
    input: {
        width: '100%',
        padding: '10px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '14px',
        boxSizing: 'border-box'
    },
    error: {
        color: '#d32f2f',
        fontSize: '12px',
        marginTop: '5px'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '30px'
    },
    required: {
        color: '#d32f2f'
    }
};

const Signup = () => {
    const [page, setPage] = useState(1);
    const [formData, setFormData] = useState({});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    const handlePage1Submit = values => {
        setFormData(values);
        setPage(2);
    };

    const formatPhoneNumber = phone => {
        // Remove all spaces and dashes
        let cleaned = phone.replace(/[\s-]/g, '');

        // If it starts with 0, replace with +44
        if (cleaned.startsWith('0')) {
            cleaned = `+44${cleaned.substring(1)}`;
        }

        // If it doesn't start with +, assume UK and add +44
        if (!cleaned.startsWith('+')) {
            cleaned = `+44${cleaned}`;
        }

        return cleaned;
    };

    const handlePage2Submit = async values => {
        setLoading(true);
        setError(null);

        try {
            const signupData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formatPhoneNumber(formData.phone),
                email: formData.email,
                organisation: formData.organisation || null,
                address: {
                    addressLine1: formData.addressLine1,
                    addressLine2: formData.addressLine2 || '',
                    townCity: formData.townCity || '',
                    postCode: formData.postCode,
                    country: formData.country
                },
                password: values.password
            };

            await signupCustomer(signupData);
            // Redirect to login page after successful signup
            history.push(`/login?email=${encodeURIComponent(formData.email)}`);
        } catch (err) {
            const errorMessage =
                err.response?.data?.message ||
                err.message ||
                'An error occurred during signup. Please try again.';
            const validationErrors = err.response?.data?.data;

            if (validationErrors && validationErrors.length > 0) {
                const errorDetails = validationErrors
                    .map(e => `${e.field}: ${e.errors.join(', ')}`)
                    .join('; ');
                setError(`${errorMessage} - ${errorDetails}`);
            } else {
                setError(errorMessage);
            }
            setLoading(false);
        }
    };

    if (page === 1) {
        return (
            <div css={css.container}>
                <h1 css={css.heading}>Create Account</h1>
                <p css={css.subheading}>Step 1 of 2 - Personal Details</p>

                <Formik
                    initialValues={{
                        firstName: formData.firstName || '',
                        lastName: formData.lastName || '',
                        phone: formData.phone || '',
                        email: formData.email || '',
                        organisation: formData.organisation || '',
                        addressLine1: formData.addressLine1 || '',
                        addressLine2: formData.addressLine2 || '',
                        townCity: formData.townCity || '',
                        postCode: formData.postCode || '',
                        country: formData.country || ''
                    }}
                    validationSchema={Page1Schema}
                    onSubmit={handlePage1Submit}
                >
                    {({ errors, touched, values, setFieldValue, handleChange }) => (
                        <Form>
                            <div css={css.formGroup}>
                                <label css={css.label} htmlFor="firstName">
                                    Full Name <span css={css.required}>*</span>
                                </label>
                                <Field
                                    css={css.input}
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    placeholder="First Name"
                                />
                                <ErrorMessage name="firstName" component="div" css={css.error} />
                            </div>

                            <div css={css.formGroup}>
                                <Field
                                    css={css.input}
                                    type="text"
                                    name="lastName"
                                    placeholder="Last Name"
                                />
                                <ErrorMessage name="lastName" component="div" css={css.error} />
                            </div>

                            <div css={css.formGroup}>
                                <label css={css.label} htmlFor="phone">
                                    Phone Number <span css={css.required}>*</span>
                                </label>
                                <Field
                                    css={css.input}
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    placeholder="e.g. 07817444971 or +447817444971"
                                />
                                <ErrorMessage name="phone" component="div" css={css.error} />
                            </div>

                            <div css={css.formGroup}>
                                <label css={css.label} htmlFor="email">
                                    Email Address <span css={css.required}>*</span>
                                </label>
                                <Field css={css.input} type="email" id="email" name="email" />
                                <ErrorMessage name="email" component="div" css={css.error} />
                            </div>

                            <div css={css.formGroup}>
                                <label css={css.label} htmlFor="organisation">
                                    Organisation Name
                                </label>
                                <Field
                                    css={css.input}
                                    type="text"
                                    id="organisation"
                                    name="organisation"
                                />
                                <ErrorMessage name="organisation" component="div" css={css.error} />
                            </div>

                            <div css={css.formGroup}>
                                <label css={css.label} htmlFor="addressLine1">
                                    Address <span css={css.required}>*</span>
                                </label>
                                {/*
                                <GooglePlacesAutocomplete
                                    id="addressLine1"
                                    name="addressLine1"
                                    value={values.addressLine1}
                                    onChange={handleChange}
                                    onPlaceSelected={addressData => {
                                        setFieldValue('addressLine1', addressData.addressLine1);
                                        setFieldValue('addressLine2', addressData.addressLine2);
                                        setFieldValue('townCity', addressData.townCity);
                                        setFieldValue('postCode', addressData.postCode);
                                        setFieldValue('country', addressData.country);
                                    }}
                                    placeholder="Enter your postcode..."
                                    css={css.input}
                                />
                                <div css={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                    Start typing to see address suggestions
                                </div>
                                <ErrorMessage name="addressLine1" component="div" css={css.error} />
                                */}
                            </div>

                            <div css={css.formGroup}>
                                <Field
                                    css={css.input}
                                    type="text"
                                    name="addressLine2"
                                    placeholder="Address Line 2"
                                />
                            </div>

                            <div css={css.formGroup}>
                                <Field
                                    css={css.input}
                                    type="text"
                                    name="townCity"
                                    placeholder="Town/City"
                                />
                            </div>

                            <div css={css.formGroup}>
                                <Field
                                    css={css.input}
                                    type="text"
                                    name="postCode"
                                    placeholder="Post Code"
                                />
                                <ErrorMessage name="postCode" component="div" css={css.error} />
                            </div>

                            <div css={css.formGroup}>
                                <Field
                                    css={css.input}
                                    type="text"
                                    name="country"
                                    placeholder="Country"
                                />
                                <ErrorMessage name="country" component="div" css={css.error} />
                            </div>

                            <div css={css.buttonContainer}>
                                <div />
                                <Button type="submit" state="primary">
                                    Next
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        );
    }

    return (
        <div css={css.container}>
            <h1 css={css.heading}>Create Account</h1>
            <p css={css.subheading}>Step 2 of 2 - Set Password</p>

            {error && (
                <div css={{ ...css.error, marginBottom: '20px', fontSize: '14px' }}>{error}</div>
            )}

            <Formik
                initialValues={{
                    password: '',
                    confirmPassword: ''
                }}
                validationSchema={Page2Schema}
                onSubmit={handlePage2Submit}
            >
                {() => (
                    <Form>
                        <div css={css.formGroup}>
                            <label css={css.label} htmlFor="password">
                                Password <span css={css.required}>*</span>
                            </label>
                            <Field
                                css={css.input}
                                type="password"
                                id="password"
                                name="password"
                                placeholder="Enter password (min 8 characters)"
                            />
                            <ErrorMessage name="password" component="div" css={css.error} />
                        </div>

                        <div css={css.formGroup}>
                            <label css={css.label} htmlFor="confirmPassword">
                                Confirm Password <span css={css.required}>*</span>
                            </label>
                            <Field
                                css={css.input}
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="Re-enter password"
                            />
                            <ErrorMessage name="confirmPassword" component="div" css={css.error} />
                        </div>

                        <div css={css.buttonContainer}>
                            <Button
                                type="button"
                                state="secondary"
                                onClick={() => setPage(1)}
                                disabled={loading}
                            >
                                Back
                            </Button>
                            <Button type="submit" state="primary" disabled={loading}>
                                {loading ? 'Creating Account...' : 'Create Account'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default Signup;
